import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Landmark, Download, Plus, FileSpreadsheet, ChevronRight, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { NuevaDeclaracionDialog } from '../components/tributario/NuevaDeclaracionDialog';
import { estadoBadge, formatBytes, formatDateTime, MES_LABEL } from '../components/tributario/tributarioUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Tributario() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [catalogo, setCatalogo] = useState({ formularios: [], estados: [] });
  const [selected, setSelected] = useState(null);
  const [decls, setDecls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [estadoF, setEstadoF] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadCatalogo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/tributario/formularios`);
      setCatalogo(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar el módulo');
    } finally { setLoading(false); }
  }, []);

  const loadDecls = useCallback(async (key) => {
    const { data } = await axios.get(`${BACKEND_URL}/api/tributario/declaraciones`, { params: { formulario: key } });
    setDecls(data);
  }, []);

  useEffect(() => { loadCatalogo(); }, [loadCatalogo]);
  useEffect(() => { if (selected) loadDecls(selected.key); }, [selected, loadDecls]);

  const eliminar = async (d) => {
    if (!window.confirm(`¿Eliminar la declaración de ${d.client_nombre} (${d.anio}${d.periodo ? '-' + d.periodo : ''})? Se perderán sus archivos.`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/tributario/declaraciones/${d._id}`); toast.success('Eliminada'); loadDecls(selected.key); loadCatalogo(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo eliminar'); }
  };

  const visibles = decls.filter((d) => (!estadoF || d.estado === estadoF) && (!q || `${d.client_nombre} ${d.nit}`.toLowerCase().includes(q.toLowerCase())));

  if (!loading && user && user.role !== 'Administrador' && !user.acceso_tributario) {
    return <div className="p-8 text-sm text-slate-600" data-testid="tributario-sin-acceso">No tienes acceso al módulo Tributario. Pídele a un administrador que lo active en tu usuario.</div>;
  }

  return (
    <div className="space-y-6" data-testid="tributario-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Landmark className="w-6 h-6 text-[#1F3864]" /> Tributario
          </h1>
          <p className="text-sm text-slate-500 mt-1">Liquidadores oficiales por cliente y año: descarga la plantilla, diligénciala en Excel, súbela con sus soportes y genera el PDF del formulario y anexos.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="tributario-cards">
        {catalogo.formularios.map((f) => {
          const active = selected?.key === f.key;
          return (
            <div key={f.key} onClick={() => setSelected(f)} role="button" tabIndex={0}
              className={`relative text-left rounded-2xl border bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md cursor-pointer ${active ? 'border-[#1F3864] ring-2 ring-[#1F3864]/15' : 'border-slate-200'}`}
              data-testid={`tributario-card-${f.key}`}>
              <span className="absolute left-0 top-5 bottom-5 w-1 rounded-r" style={{ background: f.color }} />
              <div className="flex items-start justify-between gap-3 pl-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: f.color }}>{f.periodicidad === 'mensual' ? 'Mensual' : 'Anual'}</div>
                  <h3 className="mt-0.5 text-base font-semibold text-slate-900 leading-snug">{f.nombre}</h3>
                  <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{f.descripcion}</p>
                </div>
                <FileSpreadsheet className="w-8 h-8 shrink-0 text-slate-300" />
              </div>
              <div className="mt-4 pl-3 flex items-center justify-between">
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-xs text-slate-600 bg-slate-100 rounded-full px-2 py-0.5" data-testid={`tributario-card-total-${f.key}`}>{f.total} declaraci{f.total === 1 ? 'ón' : 'ones'}</span>
                  {Object.entries(f.conteo).map(([e, n]) => <span key={e} className={`text-[10px] rounded-full border px-2 py-0.5 ${estadoBadge(e)}`}>{e}: {n}</span>)}
                </div>
                <a href={`${BACKEND_URL}/api/tributario/plantilla/${f.key}`} onClick={(e) => { e.stopPropagation(); descargarPlantilla(e, f); }}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#1F3864] hover:underline" data-testid={`tributario-plantilla-${f.key}`}>
                  <Download className="w-3.5 h-3.5" /> Plantilla ({formatBytes(f.tamano)})
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="tributario-lista">
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-5 py-4">
            <h2 className="text-base font-semibold text-slate-900">{selected.nombre}</h2>
            <span className="flex-1" />
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cliente o NIT" className="h-9 rounded-md border border-slate-200 pl-8 pr-3 text-sm" data-testid="tributario-buscar" />
            </div>
            <select value={estadoF} onChange={(e) => setEstadoF(e.target.value)} className="h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="tributario-filtro-estado">
              <option value="">Todos los estados</option>{catalogo.estados.map((e) => <option key={e}>{e}</option>)}
            </select>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#1F3864] hover:bg-[#162a4d]" data-testid="tributario-nueva-btn"><Plus className="w-4 h-4 mr-1" /> Nueva declaración</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-5 py-2.5">Cliente</th><th className="px-3 py-2.5">NIT</th><th className="px-3 py-2.5">Año / Periodo</th><th className="px-3 py-2.5">Estado</th><th className="px-3 py-2.5">Responsable</th><th className="px-3 py-2.5">Excel</th><th className="px-3 py-2.5">Soportes</th><th className="px-3 py-2.5">Actualizado</th><th className="px-3 py-2.5" /></tr>
              </thead>
              <tbody>
                {visibles.length === 0 && <tr><td colSpan={9} className="px-5 py-8 text-center text-sm text-slate-500" data-testid="tributario-lista-vacia">Aún no hay declaraciones para este formulario. Crea la primera con "Nueva declaración".</td></tr>}
                {visibles.map((d) => (
                  <tr key={d._id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/tributario/${d._id}`)} data-testid={`tributario-row-${d._id}`}>
                    <td className="px-5 py-3 font-medium text-slate-800">{d.client_nombre}</td>
                    <td className="px-3 py-3 text-slate-600">{d.nit}{d.dv ? `-${d.dv}` : ''}</td>
                    <td className="px-3 py-3 text-slate-600">{d.anio}{d.periodo ? ` · ${MES_LABEL[d.periodo] || d.periodo}` : ''}</td>
                    <td className="px-3 py-3"><span className={`text-xs rounded-full border px-2 py-0.5 ${estadoBadge(d.estado)}`}>{d.estado}</span></td>
                    <td className="px-3 py-3 text-slate-600">{d.responsable_nombre || '—'}</td>
                    <td className="px-3 py-3 text-slate-600">{d.versiones?.length ? `v${d.versiones.length}` : '—'}</td>
                    <td className="px-3 py-3 text-slate-600">{d.documentos?.length || 0}</td>
                    <td className="px-3 py-3 text-slate-500 text-xs">{formatDateTime(d.updated_at)}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      {user?.role === 'Administrador' && <button onClick={(e) => { e.stopPropagation(); eliminar(d); }} className="text-slate-400 hover:text-red-600 mr-2" title="Eliminar" data-testid={`tributario-eliminar-${d._id}`}><Trash2 className="w-4 h-4" /></button>}
                      <ChevronRight className="w-4 h-4 inline text-slate-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <NuevaDeclaracionDialog open={dialogOpen} onOpenChange={setDialogOpen} formulario={selected}
        onCreated={(d) => { loadCatalogo(); navigate(`/tributario/${d._id}`); }} />
    </div>
  );
}

async function descargarPlantilla(e, f) {
  e.preventDefault();
  try {
    toast.message('Descargando plantilla…');
    const res = await axios.get(`${BACKEND_URL}/api/tributario/plantilla/${f.key}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = f.archivo; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (err) { toast.error('No se pudo descargar la plantilla'); }
}
